import type { SvgIconComponent } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import type React from "react";

import {
	type AlertAffectedNurse,
	type AlertDetail,
	AlertSeverity,
	type AlertSortMode,
	AlertType,
	type CallInDetail,
	type DepartmentAlert,
	type DepartmentAlertDay,
	type DepartmentAlertRoleGroup,
	type DepartmentAlertShiftType,
	type HppdStaffingDetail,
	type MismatchDetail,
	type NurseInsightBadge,
	type ProblemsAlert,
	type RatioStaffingDetail,
	type RoleGroupAlertSlot,
	type WeeklyOvertimeDetail,
} from "./types";

// ── Severity Colors (dark/muted tones to differentiate from pastel alert-type colors) ──

export const SEVERITY_COLORS: Record<AlertSeverity, { accent: string; bg: string; text: string; lightBg: string }> = {
	[AlertSeverity.Critical]: { accent: "#DC2626", bg: "#FFFFFF", text: "#DC2626", lightBg: "#FEE2E2" },
	[AlertSeverity.Warning]: { accent: "#D97706", bg: "#FFFFFF", text: "#D97706", lightBg: "#FEF3C7" },
	[AlertSeverity.Major]: { accent: "#0891B2", bg: "#FFFFFF", text: "#0891B2", lightBg: "#ECFEFF" },
};

// ── Severity Icons ──────────────────────────────────────────────

export const SEVERITY_ICONS: Record<AlertSeverity, SvgIconComponent> = {
	[AlertSeverity.Critical]: ErrorOutlineIcon,
	[AlertSeverity.Warning]: WarningAmberIcon,
	[AlertSeverity.Major]: InfoOutlinedIcon,
};

// ── Alert Type Colors ────────────────────────────────────────────

export const ALERT_TYPE_COLORS: Record<AlertType, { chipBg: string; text: string; iconBg: string }> = {
	[AlertType.Overstaffed]: { chipBg: "#FEE2E2", text: "#991B1B", iconBg: "#FECACA" },
	[AlertType.CallIns]: { chipBg: "#DBEAFE", text: "#1E40AF", iconBg: "#BFDBFE" },
	[AlertType.WeeklyOvertime]: { chipBg: "#FEF3C7", text: "#92400E", iconBg: "#FDE68A" },
	[AlertType.Mismatch]: { chipBg: "#EDE9FE", text: "#5B21B6", iconBg: "#DDD6FE" },
};

// ── Alert Type Icons ─────────────────────────────────────────────

export const ALERT_TYPE_ICONS: Record<AlertType, SvgIconComponent> = {
	[AlertType.Overstaffed]: GroupRemoveIcon,
	[AlertType.CallIns]: EventBusyIcon,
	[AlertType.WeeklyOvertime]: TimerIcon,
	[AlertType.Mismatch]: SwapHorizIcon,
};

// ── Insight Badge Colors (outlined, distinct from alert type chips) ──

export const INSIGHT_BADGE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
	call_off_pickup: { bg: "#EDE9FE", text: "#7C3AED", border: "#C4B5FD" },
	request_pickup: { bg: "#DBEAFE", text: "#1D4ED8", border: "#93C5FD" },
	unscheduled: { bg: "#FFFBEB", text: "#D97706", border: "#FCD34D" },
	different_shift: { bg: "#F0FDFA", text: "#0F766E", border: "#99F6E4" },
	shift_extended: { bg: "#FFF7ED", text: "#C2410C", border: "#FDBA74" },
	late_addition: { bg: "#FFF1F2", text: "#BE123C", border: "#FDA4AF" },
	scheduled_ot: { bg: "#F0F9FF", text: "#0369A1", border: "#7DD3FC" },
	repeat_caller: { bg: "#FEF2F2", text: "#DC2626", border: "#FCA5A5" },
	filled: { bg: "#ECFDF5", text: "#059669", border: "#6EE7B7" },
	unfilled: { bg: "#FEF2F2", text: "#DC2626", border: "#FCA5A5" },
	scheduled: { bg: "#ECFDF5", text: "#059669", border: "#6EE7B7" },
	estimated_cost: { bg: "#F5F3FF", text: "#6D28D9", border: "#C4B5FD" },
};

// ── Shift Type Colors ────────────────────────────────────────────

export const SHIFT_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
	Day: { bg: "#F0F9FF", text: "#0284C7" },
	Evening: { bg: "#FFFBEB", text: "#D97706" },
	Night: { bg: "#EEF2FF", text: "#4338CA" },
};

export function getShiftCategory(startTime: string): "Day" | "Evening" | "Night" {
	const hour = Number.parseInt(startTime.split(":")[0], 10);
	if (hour >= 6 && hour < 14) return "Day";
	if (hour >= 14 && hour < 22) return "Evening";
	return "Night";
}

// ── Detail Visual Colors (used in AlertDetailVisual pills) ──────

export const DETAIL_COLORS = {
	otHours: "#F59E0B",
	nurseCount: "#6B7280",
} as const;

// ── Mismatch Reason Keys (semantic lookup for theme-based coloring) ──
// Maps backend reason strings to palette keys so AlertDetailVisual can
// resolve colors via the MUI theme without hardcoding hex values.

export const MISMATCH_REASON_PALETTE_KEYS: Record<string, "error" | "primary" | "info" | "grey"> = {
	"No show": "error",
	"Not scheduled": "primary",
	"Hours mismatch": "info",
	"Shift type changed": "primary",
	"Schedule changed": "grey",
};

// ── Alert Detail Type Guards ────────────────────────────────────
// Each guard checks for a property unique to that detail variant,
// allowing TypeScript to narrow the AlertDetail union inside switch cases.

export function isHppdDetail(detail: AlertDetail): detail is HppdStaffingDetail {
	return "hppdPercent" in detail;
}

export function isRatioDetail(detail: AlertDetail): detail is RatioStaffingDetail {
	return "ratioLadderAmount" in detail;
}

export function isCallInDetail(detail: AlertDetail): detail is CallInDetail {
	return "totalCallIns" in detail;
}

export function isWeeklyOvertimeDetail(detail: AlertDetail): detail is WeeklyOvertimeDetail {
	return "totalOtHours" in detail;
}

export function isMismatchDetail(detail: AlertDetail): detail is MismatchDetail {
	return "totalMismatchedNurses" in detail;
}

// ── Severity Ordering ────────────────────────────────────────────

const SEVERITY_RANK: Record<string, number> = {
	[AlertSeverity.Critical]: 3,
	[AlertSeverity.Warning]: 2,
	[AlertSeverity.Major]: 1,
};

export function getSeverityRank(severity: AlertSeverity | null | undefined): number {
	if (!severity) return 0;
	return SEVERITY_RANK[severity] ?? 0;
}

export function getMaxSeverityFromDay(severityCounts: Record<string, number>): AlertSeverity | null {
	if (severityCounts[AlertSeverity.Critical] > 0) return AlertSeverity.Critical;
	if (severityCounts[AlertSeverity.Warning] > 0) return AlertSeverity.Warning;
	if (severityCounts[AlertSeverity.Major] > 0) return AlertSeverity.Major;
	return null;
}

// ── i18n Key Helpers ─────────────────────────────────────────────

export const ALERT_TYPE_I18N_KEYS: Record<AlertType, string> = {
	[AlertType.Overstaffed]: "overstaffed",
	[AlertType.CallIns]: "alerts.call.ins",
	[AlertType.WeeklyOvertime]: "weekly.overtime",
	[AlertType.Mismatch]: "mismatch",
};

export const SEVERITY_I18N_KEYS: Record<AlertSeverity, string> = {
	[AlertSeverity.Critical]: "severity.critical",
	[AlertSeverity.Warning]: "severity.warning",
	[AlertSeverity.Major]: "severity.major",
};

export const ALL_ALERT_TYPES = Object.values(AlertType);
export const ALL_SEVERITIES = Object.values(AlertSeverity);

// ── Constants ───────────────────────────────────────────────────

export const AFFECTED_NURSES_DISPLAY_LIMIT = 10;

// ── Shared Counting Utilities ───────────────────────────────────

/** Sum alert type counts across all days (from pre-computed day-level counts). */
export function computeTotalAlertTypeCounts(days: DepartmentAlertDay[]): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const day of days) {
		for (const [type, count] of Object.entries(day.alertTypeCounts)) {
			counts[type] = (counts[type] ?? 0) + count;
		}
	}
	return counts;
}

/** Count severity and alert type occurrences from nested shift/role/alert structure. */
export function computeAlertCounts(shiftTypes: DepartmentAlertShiftType[]): {
	severityCounts: Record<string, number>;
	alertTypeCounts: Record<string, number>;
	totalAlerts: number;
} {
	const severityCounts: Record<string, number> = {};
	const alertTypeCounts: Record<string, number> = {};
	let totalAlerts = 0;
	for (const st of shiftTypes) {
		for (const rg of st.roleGroups) {
			for (const a of rg.alerts) {
				totalAlerts++;
				if (a.severity) severityCounts[a.severity] = (severityCounts[a.severity] ?? 0) + 1;
				alertTypeCounts[a.alertType] = (alertTypeCounts[a.alertType] ?? 0) + 1;
			}
		}
	}
	return { severityCounts, alertTypeCounts, totalAlerts };
}

/** Count severity occurrences from nested shift/role/alert structure. Accepts both original and view types. */
export function computeSeverityCounts(shiftTypes: { roleGroups: { alerts: { severity: AlertSeverity }[] }[] }[]): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const st of shiftTypes) {
		for (const rg of st.roleGroups) {
			for (const a of rg.alerts) {
				if (a.severity) counts[a.severity] = (counts[a.severity] ?? 0) + 1;
			}
		}
	}
	return counts;
}

// ── Problems Merge ──────────────────────────────────────────────

export function isProblemsAlert(slot: RoleGroupAlertSlot): slot is ProblemsAlert {
	return "alertKind" in slot && (slot as ProblemsAlert).alertKind === "problems";
}

const PROBLEM_TYPES = new Set([AlertType.Overstaffed, AlertType.Mismatch, AlertType.WeeklyOvertime]);

/**
 * When showProblems is true, combine any Overstaffed / Mismatch / WeeklyOvertime
 * alerts in the same role group into a single ProblemsAlert card.
 * Even a single problem-type alert gets wrapped so it renders as a ProblemsCard.
 * Non-problem types (CallIns) pass through unchanged.
 */
export function mergeProblemsAlerts(rg: DepartmentAlertRoleGroup): { roleGroup: string; alerts: RoleGroupAlertSlot[] } {
	const overstaffed = rg.alerts.find((a) => a.alertType === AlertType.Overstaffed);
	const mismatch = rg.alerts.find((a) => a.alertType === AlertType.Mismatch);
	const weeklyOvertime = rg.alerts.find((a) => a.alertType === AlertType.WeeklyOvertime);
	const passThrough: RoleGroupAlertSlot[] = rg.alerts.filter((a) => !PROBLEM_TYPES.has(a.alertType));

	if (overstaffed || mismatch || weeklyOvertime) {
		const candidates = [overstaffed, mismatch, weeklyOvertime].filter(Boolean) as DepartmentAlert[];
		const maxSeverity = candidates.reduce((best, a) => (getSeverityRank(a.severity) > getSeverityRank(best) ? a.severity : best), candidates[0].severity);
		const merged: ProblemsAlert = {
			alertKind: "problems",
			severity: maxSeverity,
			overstaffed,
			mismatch,
			weeklyOvertime,
		};
		return { roleGroup: rg.roleGroup, alerts: [merged, ...passThrough] };
	}

	return { roleGroup: rg.roleGroup, alerts: [...rg.alerts] };
}

// ── Sort Comparators ────────────────────────────────────────────

export const SORT_COMPARATORS: Record<AlertSortMode, (a: DepartmentAlertDay, b: DepartmentAlertDay) => number> = {
	newest: (a, b) => b.date.toMillis() - a.date.toMillis(),
	oldest: (a, b) => a.date.toMillis() - b.date.toMillis(),
	most_alerts: (a, b) => b.totalAlerts - a.totalAlerts,
	fewest_alerts: (a, b) => a.totalAlerts - b.totalAlerts,
};

// ── Shared Color Tokens ─────────────────────────────────────────

export const SCHEDULED_BADGE_COLORS = { bg: "#FFEDD5", text: "#9A3412" } as const;
export const DETAIL_BG = "#F8FAFC";
export const ROLE_GROUP_CHIP_COLORS = { bg: "#E8EAF6", text: "primary.main" } as const;

// ── Alert Summary Text ──────────────────────────────────────────

type TFn = (key: string, params?: Record<string, unknown>) => string;

export function getAlertSummaryText(alert: DepartmentAlert, t: TFn): string {
	const detail = alert.detail;
	switch (alert.alertType) {
		case AlertType.Overstaffed:
			if (isHppdDetail(detail)) return t("alerts.summary.hppd", { percent: detail.hppdPercent, target: detail.maxValidHppdPercent });
			if (isRatioDetail(detail)) return t("alerts.summary.ratio", { actual: detail.actualStaffCount, expected: detail.ratioLadderAmount });
			return t("alerts.summary.excess", { count: alert.count });
		case AlertType.CallIns:
			return `${alert.count} ${t("alerts.call.ins").toLowerCase()}`;
		case AlertType.WeeklyOvertime:
			if (isWeeklyOvertimeDetail(detail)) return t("alerts.summary.ot.detail", { hours: detail.totalOtHours, count: detail.nurseCount });
			return t("alerts.summary.ot.detail", { hours: alert.value, count: alert.count });
		case AlertType.Mismatch:
			return t("alerts.summary.mismatch", { count: alert.count });
		default:
			return t("alerts.count_plural", { count: alert.count });
	}
}

// ── Nurse Merge (ProblemsCard) ──────────────────────────────────

export type MergedNurse = AlertAffectedNurse & { sourceType: AlertType };
export type SubSection = { type: AlertType; sub: DepartmentAlert };

const INSIGHT_TO_REASONS: Record<string, string[]> = {
	unscheduled: ["Not scheduled"],
	scheduled: ["Scheduled"],
	different_shift: ["Different shift"],
	shift_extended: ["Shift extended"],
};

const REASON_TO_INSIGHT: Record<string, string> = {
	"Hours mismatch": "shift_extended",
};

/** Merge nurses from all sub-alerts into a deduplicated list with aggregated reasons and insights. */
export function mergeNurses(sections: SubSection[], t: TFn): MergedNurse[] {
	const map = new Map<number, AlertAffectedNurse & { sourceType: AlertType; reasonSet: Set<string>; insightMap: Map<string, NurseInsightBadge> }>();

	for (const s of sections) {
		for (const nurse of s.sub.detail?.affectedNurses ?? []) {
			const existing = map.get(nurse.nurseId);
			if (existing) {
				existing.reasonSet.add(nurse.reason);
				for (const insight of nurse.insights ?? []) {
					if (!existing.insightMap.has(insight.insightType)) {
						existing.insightMap.set(insight.insightType, insight);
					}
				}
				if (nurse.weeklyOtHours > existing.weeklyOtHours) existing.weeklyOtHours = nurse.weeklyOtHours;
				if (nurse.hourlyWage && (!existing.hourlyWage || nurse.hourlyWage > existing.hourlyWage)) existing.hourlyWage = nurse.hourlyWage;
			} else {
				map.set(nurse.nurseId, {
					...nurse,
					sourceType: s.type,
					reasonSet: new Set([nurse.reason]),
					insightMap: new Map((nurse.insights ?? []).map((i) => [i.insightType, i])),
				});
			}
		}
	}

	return [...map.values()]
		.filter(({ insightMap }) => !insightMap.has("call_off_pickup"))
		.map(({ reasonSet, insightMap, ...rest }) => {
			const consumedInsights = new Set<string>();
			const reasons: string[] = [];

			for (const reason of reasonSet) {
				const insightType = REASON_TO_INSIGHT[reason];
				const insight = insightType ? insightMap.get(insightType) : undefined;
				if (insight) {
					reasons.push(t(insight.i18nKey, insight.i18nParams));
					consumedInsights.add(insightType);
				} else {
					reasons.push(reason);
				}
			}

			const reasonLower = new Set(reasons.map((r) => r.toLowerCase()));
			const filteredInsights = [...insightMap.values()].filter((insight) => {
				if (consumedInsights.has(insight.insightType)) return false;
				const mappedReasons = INSIGHT_TO_REASONS[insight.insightType];
				return !mappedReasons || !mappedReasons.some((r) => reasonLower.has(r.toLowerCase()));
			});

			return { ...rest, reason: reasons.join(", "), insights: filteredInsights };
		});
}

// ── Accessibility ──────────────────────────────────────────────

/** Spread onto a clickable `<Box>` to make it keyboard-accessible (Enter / Space). */
export const keyboardClickProps = (onClick: () => void) => ({
	role: "button" as const,
	tabIndex: 0,
	onKeyDown: (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			onClick();
		}
	},
});
